import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { Phone, MessageCircle, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");

  useEffect(() => {
    axios.get(`${API}/faqs`).then(res => setFaqs(res.data)).catch(console.error);
  }, []);

  const categories = ["Alle", ...new Set(faqs.map(f => f.category))];
  const filteredFaqs = activeCategory === "Alle" ? faqs : faqs.filter(f => f.category === activeCategory);

  return (
    <>
      <SEO 
        title="FAQ - Häufig gestellte Fragen"
        description="Antworten auf häufige Fragen zu Kfz-Gutachten: Kosten, Ablauf, Rechte nach Unfall, Wertminderung, Versicherung. Alles was Sie wissen müssen."
        canonical="/faq"
        keywords="Kfz Gutachten FAQ, häufige Fragen, Unfallgutachten Kosten, Versicherung"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-[#AA6622] font-bold text-sm uppercase tracking-wider mb-4">FAQ</span>
            <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
              Häufig gestellte Fragen
            </h1>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Hier finden Sie Antworten auf die wichtigsten Fragen rund um Kfz-Gutachten, Unfallabwicklung und Ihre Rechte als Geschädigter.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-sm font-medium transition-colors ${
                  activeCategory === cat
                    ? "bg-[#AA6622] text-white"
                    : "bg-[#F7F7F7] text-[#4A4A4A] hover:text-black"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {filteredFaqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-[#F7F7F7] rounded-sm border-none"
                data-testid={`faq-item-${faq.id}`}
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-gray-100 font-bold text-left text-black text-lg">
                  <div className="flex items-start space-x-3">
                    <HelpCircle className="w-5 h-5 text-[#AA6622] flex-shrink-0 mt-0.5" />
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5 text-[#4A4A4A] leading-relaxed pl-14">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#4A4A4A]">Keine Fragen in dieser Kategorie gefunden.</p>
            </div>
          )}
        </div>
      </section>

      {/* Still Questions? */}
      <section className="py-20 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Ihre Frage nicht dabei?</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Kein Problem! Kontaktieren Sie uns gerne direkt – wir beantworten alle Ihre Fragen persönlich.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
              <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
            </a>
            <Link to="/kontakt" className="border-2 border-black text-black px-8 py-4 rounded-sm font-bold flex items-center justify-center hover:bg-black hover:text-white transition-colors">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
